import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ArrowsLeftRight, Warning, X, Camera, UploadSimple, FileText, Trash, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { bookingApi, formatApiError } from '../services/api';

const REASONS = [
  { value: 'defekt', label: 'Defekt' },
  { value: 'unfall', label: 'Unfall' },
  { value: 'wartung', label: 'Wartung erforderlich' },
  { value: 'kundenwunsch', label: 'Kundenwunsch' },
  { value: 'sonstige', label: 'Sonstige' },
];

const DEFAULT_SLOTS = [
  { key: 'front',       label: 'Front' },
  { key: 'rear',        label: 'Heck' },
  { key: 'side_left',   label: 'Seite links' },
  { key: 'side_right',  label: 'Seite rechts' },
  { key: 'interior',    label: 'Innenraum' },
  { key: 'odometer',    label: 'Tacho / Kilometerstand' },
  { key: 'fuel_gauge',  label: 'Tankanzeige' },
  { key: 'extra',       label: 'Zusatzfoto' },
];

const VehicleSwapDialog = ({ booking, currentVehicle, open, onOpenChange, onSwapped }) => {
  const [step, setStep] = useState(1);            // 1 = tausch, 2 = photos+pdf
  const [candidates, setCandidates] = useState([]);
  const [loadingCands, setLoadingCands] = useState(false);
  const [newVehicleId, setNewVehicleId] = useState('');
  const [reason, setReason] = useState('defekt');
  const [notes, setNotes] = useState('');
  const [mileageOldEnd, setMileageOldEnd] = useState('');
  const [mileageNewStart, setMileageNewStart] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [createdSwap, setCreatedSwap] = useState(null);   // {id, ...}
  const [photos, setPhotos] = useState([]);               // photos of current swap
  const [uploading, setUploading] = useState(false);
  const [phase, setPhase] = useState('old_return');       // old_return | new_handover
  const [slotKey, setSlotKey] = useState('odometer');
  const fileRef = useRef(null);

  const loadCandidates = useCallback(async () => {
    if (!booking?.id) return;
    setLoadingCands(true);
    try {
      const res = await bookingApi.getSwapCandidates(booking.id);
      setCandidates(res.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingCands(false);
    }
  }, [booking?.id]);

  useEffect(() => {
    if (open) {
      setStep(1);
      setNewVehicleId('');
      setReason('defekt');
      setNotes('');
      setMileageOldEnd(currentVehicle?.current_mileage != null ? String(currentVehicle.current_mileage) : '');
      setMileageNewStart('');
      setCreatedSwap(null);
      setPhotos([]);
      setPhase('old_return');
      setSlotKey('odometer');
      loadCandidates();
    }
  }, [open, currentVehicle?.current_mileage, loadCandidates]);

  const selectedCandidate = candidates.find((c) => c.id === newVehicleId);

  useEffect(() => {
    if (selectedCandidate && selectedCandidate.current_mileage != null) {
      setMileageNewStart(String(selectedCandidate.current_mileage));
    } else if (selectedCandidate) {
      setMileageNewStart('');
    }
  }, [selectedCandidate]);

  const handleSubmit = async () => {
    if (!newVehicleId) { toast.error('Bitte ein Ersatzfahrzeug auswählen'); return; }
    const oldEnd = parseInt(mileageOldEnd, 10);
    const newStart = parseInt(mileageNewStart, 10);
    if (Number.isNaN(oldEnd) || oldEnd < 0) { toast.error('End-km altes Fahrzeug eingeben'); return; }
    if (Number.isNaN(newStart) || newStart < 0) { toast.error('Start-km Ersatzfahrzeug eingeben'); return; }
    setSubmitting(true);
    try {
      const res = await bookingApi.swapVehicle(booking.id, {
        new_vehicle_id: newVehicleId, reason,
        notes: notes.trim() || null,
        mileage_old_end: oldEnd, mileage_new_start: newStart,
      });
      toast.success(res.data.message || 'Fahrzeug getauscht', {
        description: `Gefahren mit altem Auto: ${res.data.swap.from_driven_km || 0} km`,
      });
      setCreatedSwap(res.data.swap);
      setStep(2);
      if (onSwapped) await onSwapped(res.data.booking);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (!file || !createdSwap) return;
    setUploading(true);
    try {
      const r = await bookingApi.uploadSwapPhoto(booking.id, createdSwap.id, phase, slotKey, file);
      setPhotos(p => [...p, r.data]);
      toast.success('Foto hochgeladen');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (photoId) => {
    try {
      await bookingApi.deleteSwapPhoto(booking.id, createdSwap.id, photoId);
      setPhotos(p => p.filter(x => x.photo_id !== photoId));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleFinish = () => {
    onOpenChange(false);
    if (onSwapped) onSwapped();  // trigger re-fetch in parent
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-auto" data-testid="swap-vehicle-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowsLeftRight size={20} className="text-[#E60000]" weight="bold" />
            {step === 1 ? 'Fahrzeug während der Miete tauschen' : 'Foto-Dokumentation & Vertragsanhang'}
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            {/* Current vehicle info */}
            <div className="bg-[#FFF5F5] border border-[#FECACA] rounded-sm p-3">
              <p className="text-xs text-[#8A94A6] mb-1">Aktuelles Fahrzeug</p>
              <p className="text-sm font-semibold text-[#111418]">
                {currentVehicle ? `${currentVehicle.brand} ${currentVehicle.model} (${currentVehicle.license_plate})` : '—'}
              </p>
              {currentVehicle?.current_mileage != null && (
                <p className="text-xs text-[#525866] mt-0.5">
                  Aktueller km-Stand: <span className="font-mono">{currentVehicle.current_mileage.toLocaleString('de-DE')} km</span>
                </p>
              )}
              {booking?.included_km != null && (
                <p className="text-xs text-[#525866] mt-0.5">
                  Inklusive Kilometer laut Vertrag: <span className="font-mono">{Number(booking.included_km).toLocaleString('de-DE')} km</span>
                </p>
              )}
            </div>

            <div>
              <Label className="overline mb-1 block">Grund für den Tausch *</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="swap-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REASONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="overline mb-1 block">Notiz (optional)</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="z.B. Motorschaden, Werkstatt informiert…" rows={2}
                data-testid="swap-notes" />
            </div>

            <div>
              <Label className="overline mb-1 block">Ersatzfahrzeug *</Label>
              {loadingCands ? (
                <p className="text-sm text-[#8A94A6]">Lade verfügbare Fahrzeuge…</p>
              ) : candidates.length === 0 ? (
                <p className="text-sm text-[#E60000] flex items-center gap-1.5"><Warning size={14} /> Keine anderen Fahrzeuge gefunden.</p>
              ) : (
                <Select value={newVehicleId} onValueChange={setNewVehicleId}>
                  <SelectTrigger data-testid="swap-new-vehicle-select">
                    <SelectValue placeholder={`Auswählen (${candidates.filter(c => c.available).length} verfügbar)`} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {candidates.filter((c) => c.available).map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`swap-candidate-${c.id}`}>
                        {c.label} {c.current_mileage != null && `· ${c.current_mileage.toLocaleString('de-DE')} km`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline mb-1 block">End-km altes Fahrzeug *</Label>
                <Input type="number" min={0} value={mileageOldEnd} onChange={(e) => setMileageOldEnd(e.target.value)}
                  placeholder="z.B. 78450" data-testid="swap-old-mileage-input" />
                <p className="text-xs text-[#8A94A6] mt-1">Gefahren = End-km − Start-km bei Übernahme</p>
              </div>
              <div>
                <Label className="overline mb-1 block">Start-km Ersatzfahrzeug *</Label>
                <Input type="number" min={0} value={mileageNewStart} onChange={(e) => setMileageNewStart(e.target.value)}
                  placeholder="z.B. 32100" data-testid="swap-new-mileage-input" />
              </div>
            </div>

            <div className="bg-[#F1F2F4] rounded-sm p-3 text-xs text-[#525866]">
              <strong className="text-[#111418]">Nach dem Tausch:</strong>
              <ul className="list-disc pl-4 mt-1 space-y-0.5">
                <li>Foto-Dokumentation (Rückgabe altes + Übergabe neues Fahrzeug)</li>
                <li>PDF-Nachtrag zum Mietvertrag mit Kilometer-Aggregation und Fotos</li>
                <li>Preis bleibt unverändert · Vertrag läuft normal bis zum Rückgabedatum</li>
              </ul>
            </div>
          </div>
        )}

        {step === 2 && createdSwap && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
              <p className="text-sm font-semibold text-emerald-800 flex items-center gap-1">
                <CheckCircle weight="bold" size={16}/> Tausch #{createdSwap.swap_index} erfolgreich
              </p>
              <p className="text-xs text-emerald-800 mt-1">
                {createdSwap.from_vehicle_label} → {createdSwap.to_vehicle_label}
              </p>
              <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-[#111418]">
                <div className="bg-white rounded p-2">
                  <p className="text-[10px] text-[#8A94A6] uppercase">Gefahren (altes Fahrzeug)</p>
                  <p className="font-mono font-bold">{(createdSwap.from_driven_km ?? 0).toLocaleString('de-DE')} km</p>
                  <p className="text-[10px] text-[#8A94A6]">{createdSwap.from_mileage_start?.toLocaleString('de-DE')} → {createdSwap.from_mileage_end?.toLocaleString('de-DE')} km</p>
                </div>
                <div className="bg-white rounded p-2">
                  <p className="text-[10px] text-[#8A94A6] uppercase">Gesamt gefahren / Inkl.</p>
                  <p className="font-mono font-bold">
                    {(createdSwap.total_driven_km_after_swap ?? 0).toLocaleString('de-DE')} /{' '}
                    {createdSwap.included_km_total != null ? createdSwap.included_km_total.toLocaleString('de-DE') : '∞'} km
                  </p>
                  <p className={`text-[10px] font-semibold ${createdSwap.included_km_exceeded ? 'text-red-700' : 'text-emerald-700'}`}>
                    {createdSwap.included_km_exceeded ? 'Inkl.-km bereits überschritten' : 'Inkl.-km im grünen Bereich'}
                  </p>
                </div>
              </div>
            </div>

            {/* Foto-Upload */}
            <div className="border border-[#E2E4E9] rounded p-3">
              <p className="text-sm font-semibold mb-2">Foto-Dokumentation</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                <div>
                  <Label className="overline mb-1 block">Phase</Label>
                  <Select value={phase} onValueChange={setPhase}>
                    <SelectTrigger data-testid="swap-photo-phase"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="old_return">Rückgabe altes Fahrzeug</SelectItem>
                      <SelectItem value="new_handover">Übergabe Ersatzfahrzeug</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="overline mb-1 block">Foto-Slot</Label>
                  <Select value={slotKey} onValueChange={setSlotKey}>
                    <SelectTrigger data-testid="swap-photo-slot"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DEFAULT_SLOTS.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="swap-photo-upload-btn">
                  <UploadSimple weight="bold" size={14} className="mr-1"/> Datei
                </Button>
                <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}>
                  <Camera weight="bold" size={14} className="mr-1"/> Kamera
                </Button>
                <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = null; uploadPhoto(f); }}
                  data-testid="swap-photo-file-input"
                />
              </div>

              {photos.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                  {photos.map(p => (
                    <div key={p.photo_id} className="relative border border-[#E2E4E9] rounded overflow-hidden">
                      <img src={bookingApi.swapPhotoUrl(booking.id, createdSwap.id, p.photo_id)} alt={p.slot_label} className="w-full h-20 object-cover"/>
                      <button onClick={() => removePhoto(p.photo_id)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"><Trash size={10} weight="bold"/></button>
                      <p className="text-[10px] text-[#525866] px-1 py-0.5 truncate">{p.phase === 'old_return' ? 'ALT' : 'NEU'} · {p.slot_label}</p>
                    </div>
                  ))}
                </div>
              )}
              {photos.length === 0 && <p className="text-xs text-[#8A94A6] mt-2">Noch keine Fotos hochgeladen. Optional – kann später auf der Buchungsseite ergänzt werden.</p>}
            </div>

            {/* PDF-Nachtrag */}
            <div className="flex items-center gap-2">
              <a href={bookingApi.swapContractPdfUrl(booking.id, createdSwap.id)}
                 target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#E60000] text-[#E60000] hover:bg-[#E60000]/5 rounded text-sm"
                 data-testid="swap-pdf-link"
              >
                <FileText weight="bold" size={14}/> Vertrags-Nachtrag PDF öffnen
              </a>
              <p className="text-xs text-[#8A94A6]">Wird bei jedem Aufruf mit den aktuellen Fotos neu generiert.</p>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === 1 && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="swap-cancel-btn">
                <X size={14} className="mr-1" /> Abbrechen
              </Button>
              <Button className="btn-primary" onClick={handleSubmit}
                disabled={submitting || !newVehicleId || !mileageOldEnd || !mileageNewStart}
                data-testid="swap-confirm-btn">
                <ArrowsLeftRight size={14} className="mr-1" />
                {submitting ? 'Tausche…' : 'Fahrzeug tauschen'}
              </Button>
            </>
          )}
          {step === 2 && (
            <Button className="btn-primary" onClick={handleFinish} data-testid="swap-finish-btn">
              <CheckCircle size={14} className="mr-1"/> Fertig
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VehicleSwapDialog;
